/**
 * The entry-handled `subscriptions/listen` router for the HTTP serving entry.
 *
 * `createMcpHandler` recognizes a modern-classified `subscriptions/listen`
 * request and routes it here: the entry owns ack-first, per-stream filtering,
 * subscription-id stamping, keepalive, capacity guarding, and teardown. The
 * consumer's factory IS constructed for listen, to read the instance's
 * declared `ServerCapabilities` only — the probe instance is never connected
 * and is closed immediately after the capabilities read. Token verification
 * and any per-request authorization still belong at the middleware layer
 * mounted in front of `createMcpHandler` (the entry's documented authz
 * posture).
 *
 * Per the spec at protocol revision 2026-07-28:
 * - The acknowledged notification is the FIRST message on the stream and
 *   carries the honored subset of the requested filter.
 * - Every notification on the stream (including the ack) carries the listen
 *   request's JSON-RPC id under `_meta['io.modelcontextprotocol/subscriptionId']`.
 * - The server MUST NOT deliver a notification type the client did not request.
 * - Server-side graceful close (`closeAll()`) emits the empty
 *   `subscriptions/listen` JSON-RPC result (the `SubscriptionsListenResult` —
 *   `_meta` carries the subscription id) before closing the stream; an abrupt
 *   transport close carries no response and the client treats it as a
 *   disconnect.
 */
import type {
    Implementation,
    JSONRPCRequest,
    RequestId,
    ServerCapabilities,
    SubscriptionFilter
} from '@modelcontextprotocol/core-internal';
import { codecForVersion, MODERN_WIRE_REVISION, SERVER_INFO_META_KEY, SUBSCRIPTION_ID_META_KEY } from '@modelcontextprotocol/core-internal';

import type { ServerEventBus } from './serverEventBus';
import { honoredSubset, listenFilterAccepts, serverEventToNotification } from './serverEventBus';
import { armSseKeepAlive, DEFAULT_SSE_KEEP_ALIVE_MS } from './sseKeepAlive';

/** Default capacity guard: refuse a new subscription when this many are already open. */
export const DEFAULT_MAX_SUBSCRIPTIONS = 1024;

/** Options for {@linkcode createListenRouter}. */
export interface ListenRouterOptions {
    /** The event bus listen streams subscribe to. */
    bus: ServerEventBus;
    /** Reject a new listen with `-32603` when this many subscriptions are already open (default 1024). */
    maxSubscriptions?: number;
    /** SSE comment-frame keepalive interval; `0` disables keepalive (default 15000). */
    keepAliveMs?: number;
    /** Out-of-band error reporting (never alters the response). */
    onerror?: (error: Error) => void;
}

/**
 * A wire-shape notification body (method + loose params).
 * @internal
 */
export interface NotificationBody {
    method: string;
    params: { _meta?: Record<string, unknown>; [key: string]: unknown };
}

function jsonRpcError(id: RequestId | null, code: number, message: string): Response {
    return Response.json({ jsonrpc: '2.0', error: { code, message }, id }, { status: 200 });
}

/** Stamp the subscription id onto a notification's `_meta`. Non-mutating. */
function stampSubscriptionId(
    notification: { method: string; params?: { _meta?: Record<string, unknown>; [key: string]: unknown } },
    subscriptionId: RequestId
): NotificationBody {
    return {
        method: notification.method,
        params: {
            ...notification.params,
            _meta: { ...notification.params?._meta, [SUBSCRIPTION_ID_META_KEY]: subscriptionId }
        }
    };
}

/**
 * Read the requested filter off a `subscriptions/listen` request body.
 * Returns the validated filter, or `undefined` when `params.notifications`
 * is absent or fails the schema (the caller answers `-32602` — the spec
 * marks `notifications` REQUIRED on the listen request).
 */
export function parseListenFilter(message: JSONRPCRequest): SubscriptionFilter | undefined {
    // `subscriptions/listen` is 2026-only vocabulary; route through the era
    // codec's request validator (the wire layer owns the filter schema).
    const outcome = codecForVersion(MODERN_WIRE_REVISION).validateRequest('subscriptions/listen', message);
    return outcome.ok ? outcome.value.params?.notifications : undefined;
}

/**
 * The HTTP listen router: holds the set of open subscriptions and serves
 * each listen request as an SSE response.
 */
export interface ListenRouter {
    /**
     * Serve one `subscriptions/listen` request and return the SSE `Response`
     * (or, on capacity / params rejection, the in-band JSON-RPC error
     * `Response`). The ack notification is the first SSE frame.
     *
     * `capabilities` is required: the acknowledged filter is always narrowed
     * against what the serving instance advertises (honoring a filter without
     * capabilities would fail open and deliver unadvertised types).
     * `serverInfo` is the serving instance's identity, stamped onto the
     * graceful-close result's `_meta` (the spec's `SubscriptionsListenResultMeta`
     * extends `ResultMetaObject`, so the serverInfo SHOULD applies there too).
     */
    serve(message: JSONRPCRequest, signal: AbortSignal | undefined, capabilities: ServerCapabilities, serverInfo: Implementation): Response;
    /**
     * Gracefully close every open subscription stream: emits the empty
     * `subscriptions/listen` JSON-RPC result (the spec's graceful-close
     * signal) as the final SSE frame, then closes the stream.
     */
    closeAll(): void;
    /** The number of currently open subscription streams (for tests / introspection). */
    readonly openCount: number;
}

export function createListenRouter(options: ListenRouterOptions): ListenRouter {
    const { bus, onerror } = options;
    const maxSubscriptions = options.maxSubscriptions ?? DEFAULT_MAX_SUBSCRIPTIONS;
    const keepAliveMs = options.keepAliveMs ?? DEFAULT_SSE_KEEP_ALIVE_MS;

    const open = new Set<(graceful: boolean) => void>();

    function serve(
        message: JSONRPCRequest,
        signal: AbortSignal | undefined,
        capabilities: ServerCapabilities,
        serverInfo: Implementation
    ): Response {
        // Capacity guard, pre-ack: in-band -32603 on HTTP 200.
        if (open.size >= maxSubscriptions) {
            onerror?.(new Error(`subscriptions/listen refused: subscription limit reached (${maxSubscriptions})`));
            return jsonRpcError(message.id, -32_603, 'Subscription limit reached');
        }
        const filter = parseListenFilter(message);
        if (filter === undefined) {
            return jsonRpcError(message.id, -32_602, "Invalid params: 'notifications' is required and must be a valid SubscriptionFilter");
        }
        const honored = honoredSubset(filter, capabilities);
        // The spec carries the listen request's JSON-RPC id verbatim as the
        // subscription id; demux is per-connection (each HTTP listen has its
        // own SSE stream) so client-chosen ids cannot route across requests.
        const subscriptionId = message.id;

        const encoder = new TextEncoder();
        let controller!: ReadableStreamDefaultController<Uint8Array>;
        let closed = false;
        let unsubscribe: (() => void) | undefined;
        let keepAliveTimer: ReturnType<typeof setInterval> | undefined;
        let abortCleanup: (() => void) | undefined;

        const writeFrame = (frame: string) => {
            if (closed) return;
            try {
                controller.enqueue(encoder.encode(frame));
            } catch (error) {
                onerror?.(error instanceof Error ? error : new Error(String(error)));
            }
        };
        const writeNotification = (method: string, params: { _meta?: Record<string, unknown>; [key: string]: unknown }) => {
            writeFrame(`event: message\ndata: ${JSON.stringify({ jsonrpc: '2.0', method, params })}\n\n`);
        };

        const teardown = (graceful: boolean) => {
            if (closed) return;
            if (graceful) {
                // Server-side graceful close: emit the empty
                // `subscriptions/listen` JSON-RPC result before closing the
                // stream so the client distinguishes graceful end from a
                // transport drop. Written before `closed = true` so writeFrame
                // still enqueues.
                writeFrame(
                    `event: message\ndata: ${JSON.stringify({
                        jsonrpc: '2.0',
                        id: subscriptionId,
                        result: {
                            resultType: 'complete',
                            _meta: { [SUBSCRIPTION_ID_META_KEY]: subscriptionId, [SERVER_INFO_META_KEY]: serverInfo }
                        }
                    })}\n\n`
                );
            }
            closed = true;
            try {
                unsubscribe?.();
            } catch (error) {
                onerror?.(error instanceof Error ? error : new Error(String(error)));
            }
            if (keepAliveTimer !== undefined) clearInterval(keepAliveTimer);
            abortCleanup?.();
            open.delete(teardown);
            try {
                controller.close();
            } catch {
                // Already closed/cancelled by the consumer.
            }
        };

        const readable = new ReadableStream<Uint8Array>({
            start(streamController) {
                controller = streamController;

                // Ack-first MUST: the acknowledged notification is the first
                // frame on the stream, stamped with the subscription id.
                const ack = stampSubscriptionId(
                    { method: 'notifications/subscriptions/acknowledged', params: { notifications: honored } },
                    subscriptionId
                );
                writeNotification(ack.method, ack.params);

                // Only after the ack frame is enqueued does delivery activate.
                unsubscribe = bus.subscribe(event => {
                    if (closed || !listenFilterAccepts(honored, event)) return;
                    const note = stampSubscriptionId(serverEventToNotification(event), subscriptionId);
                    writeNotification(note.method, note.params);
                });

                keepAliveTimer = armSseKeepAlive(keepAliveMs, () => writeFrame(': keepalive\n\n'));

                open.add(teardown);
            },
            cancel() {
                // The client closed the SSE stream — the spec's HTTP cancel
                // signal. Not a server-side graceful close, so no listen
                // result is written (and the consumer is gone anyway).
                teardown(false);
            }
        });

        if (signal !== undefined) {
            if (signal.aborted) {
                teardown(false);
            } else {
                const onAbort = () => teardown(false);
                signal.addEventListener('abort', onAbort, { once: true });
                abortCleanup = () => signal.removeEventListener('abort', onAbort);
            }
        }

        return new Response(readable, {
            status: 200,
            headers: {
                'Content-Type': 'text/event-stream',
                'Cache-Control': 'no-cache, no-transform',
                Connection: 'keep-alive',
                'X-Accel-Buffering': 'no'
            }
        });
    }

    return {
        serve,
        closeAll() {
            for (const teardown of open) teardown(true);
        },
        get openCount() {
            return open.size;
        }
    };
}

/* ------------------------------------------------------------------------ *
 * Stdio listen router
 * ------------------------------------------------------------------------ */

/** A graceful-close `subscriptions/listen` result frame emitted by {@linkcode StdioListenRouter.teardownAll}. */
export interface ListenCloseFrame {
    jsonrpc: '2.0';
    id: RequestId;
    result: {
        resultType: 'complete';
        _meta: { [SUBSCRIPTION_ID_META_KEY]: RequestId; [SERVER_INFO_META_KEY]?: Implementation };
    };
}

const CHANGE_NOTIFICATION_METHODS: ReadonlySet<string> = new Set([
    'notifications/tools/list_changed',
    'notifications/prompts/list_changed',
    'notifications/resources/list_changed',
    'notifications/resources/updated'
]);

/**
 * Per-connection listen state for the stdio entry. One instance is held by
 * `serveStdio` for the connection lifetime; it routes inbound
 * `subscriptions/listen` / `notifications/cancelled` and rewrites outbound
 * change notifications onto the active subscriptions. No bus — the long-lived
 * pinned instance's existing `send*ListChanged()` calls feed straight into
 * `routeOutbound()`.
 */
export class StdioListenRouter {
    /** Active subscriptions, keyed by the listen request's JSON-RPC id verbatim. */
    private readonly _subs = new Map<RequestId, SubscriptionFilter>();
    /**
     * The serving instance's declared capabilities. Filled in by the entry
     * once the modern instance is constructed (the router is created before
     * the instance exists), so the acknowledged filter is narrowed against
     * what the server can actually deliver.
     */
    private _serverCapabilities: ServerCapabilities | undefined;
    /**
     * The serving instance's identity, stamped onto the graceful-close
     * results' `_meta` (the spec's `SubscriptionsListenResultMeta` extends
     * `ResultMetaObject`). Handed over together with the capabilities.
     */
    private _serverInfo: Implementation | undefined;

    constructor(
        private readonly _maxSubscriptions: number = DEFAULT_MAX_SUBSCRIPTIONS,
        serverCapabilities?: ServerCapabilities,
        serverInfo?: Implementation
    ) {
        this._serverCapabilities = serverCapabilities;
        this._serverInfo = serverInfo;
    }

    /**
     * Record the serving instance's declared capabilities and identity once
     * it has been constructed. Called by `serveStdio`'s connect path;
     * subsequent `serve()` calls narrow the honored filter against the
     * capabilities, and `teardownAll()` stamps the identity.
     */
    setServerCapabilities(capabilities: ServerCapabilities, serverInfo?: Implementation): void {
        this._serverCapabilities = capabilities;
        if (serverInfo !== undefined) this._serverInfo = serverInfo;
    }

    /** Whether `id` is an active listen subscription on this connection. */
    has(id: RequestId): boolean {
        return this._subs.has(id);
    }

    /**
     * Serve one inbound `subscriptions/listen` request: registers the
     * subscription and returns the stamped acknowledged notification (or, on
     * capacity / params rejection, the in-band JSON-RPC error response).
     *
     * @throws when called before {@linkcode setServerCapabilities} (or the
     * constructor) has supplied the serving instance's capabilities. Honoring a
     * filter without knowing the server's advertised capabilities would fail
     * open (deliver unadvertised types); the entry guarantees capabilities are
     * set before any listen request is routed here.
     */
    serve(message: JSONRPCRequest): NotificationBody | { jsonrpc: '2.0'; id: RequestId; error: { code: number; message: string } } {
        if (this._serverCapabilities === undefined) {
            throw new Error(
                'StdioListenRouter.serve() called before setServerCapabilities(); refusing to honor a filter without capabilities'
            );
        }
        if (this._subs.size >= this._maxSubscriptions) {
            return { jsonrpc: '2.0', id: message.id, error: { code: -32_603, message: 'Subscription limit reached' } };
        }
        const filter = parseListenFilter(message);
        if (filter === undefined) {
            return {
                jsonrpc: '2.0',
                id: message.id,
                error: { code: -32_602, message: "Invalid params: 'notifications' is required and must be a valid SubscriptionFilter" }
            };
        }
        const honored = honoredSubset(filter, this._serverCapabilities);
        this._subs.set(message.id, honored);
        return stampSubscriptionId({ method: 'notifications/subscriptions/acknowledged', params: { notifications: honored } }, message.id);
    }

    /**
     * Tear down one subscription (inbound `notifications/cancelled`). Returns
     * `true` when a subscription was removed. After this call NOTHING further
     * is delivered for that subscription id (the post-cancel hardening).
     */
    cancel(id: RequestId): boolean {
        return this._subs.delete(id);
    }

    /**
     * Route an outbound notification through the active subscriptions.
     *
     * - For a subscription-gated change notification, returns one stamped copy
     *   per subscription that opted in to it (an empty array means it is
     *   dropped — the modern era never delivers an un-requested change type).
     * - For any other outbound message, returns `'passthrough'` (the entry
     *   forwards it as-is).
     */
    routeOutbound(message: { method: string; params?: { [key: string]: unknown } }): NotificationBody[] | 'passthrough' {
        if (!CHANGE_NOTIFICATION_METHODS.has(message.method)) {
            return 'passthrough';
        }
        const uriParam: unknown = message.params?.['uri'];
        const uri = typeof uriParam === 'string' ? uriParam : undefined;
        const event = notificationToServerEvent(message.method, uri);
        const out: NotificationBody[] = [];
        for (const [subscriptionId, filter] of this._subs) {
            if (listenFilterAccepts(filter, event)) {
                out.push(stampSubscriptionId({ method: message.method, params: message.params ?? {} }, subscriptionId));
            }
        }
        return out;
    }

    /**
     * Server-side graceful teardown of every active subscription: returns the
     * empty `subscriptions/listen` JSON-RPC result for each subscription id —
     * the spec's graceful-close signal, `_meta` carrying the subscription id
     * and the serving instance's identity — for the entry to emit before
     * closing the wire. Clears the set so nothing further is delivered.
     */
    teardownAll(): ListenCloseFrame[] {
        const out: ListenCloseFrame[] = [];
        for (const id of this._subs.keys()) {
            out.push({
                jsonrpc: '2.0',
                id,
                result: {
                    resultType: 'complete',
                    _meta: {
                        [SUBSCRIPTION_ID_META_KEY]: id,
                        ...(this._serverInfo !== undefined && { [SERVER_INFO_META_KEY]: this._serverInfo })
                    }
                }
            });
        }
        this._subs.clear();
        return out;
    }
}

function notificationToServerEvent(method: string, uri: string | undefined): import('./serverEventBus').ServerEvent {
    switch (method) {
        case 'notifications/tools/list_changed': {
            return { kind: 'tools_list_changed' };
        }
        case 'notifications/prompts/list_changed': {
            return { kind: 'prompts_list_changed' };
        }
        case 'notifications/resources/list_changed': {
            return { kind: 'resources_list_changed' };
        }
        default: {
            return { kind: 'resource_updated', uri: uri ?? '' };
        }
    }
}
